import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, message, Skeleton, Space, Tag, Typography, Modal } from "antd";
import {
  AppstoreAddOutlined,
  ArrowLeftOutlined,
  CloseOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  SendOutlined,
  ShareAltOutlined,
  SolutionOutlined,
  TeamOutlined,
  EditOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import SignInRequiredModal from "components/auth/SignInRequiredModal";
import paths from "routes/paths";
import { olreadiaService } from "services/olreadiaService";
import { bearerGuestToken, ensureGuestSession } from "utils/guestSession";
import { hasRichText, richTextMarkup } from "./richTextUtils";
import "./JobOpportunityDetails.css";

const { Text, Title } = Typography;

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const titleCase = (value) =>
  String(value || "")
    .replace(/[_-]/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const jobPostEditPath = (jobId) => `${paths.newJobPost}?jobOpportunityId=${encodeURIComponent(jobId)}`;

const isRawReferenceId = (value) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value || "").trim());

const readableText = (value) => {
  const text = firstText(value);
  return text && !isRawReferenceId(text) ? text : "";
};

const namedRefLabel = (value) => {
  if (!value) return "";
  if (typeof value === "string") return isRawReferenceId(value) ? "" : titleCase(value);
  return readableText(value.name) || readableText(value.label) || readableText(value.title) || readableText(value.code);
};

const normalizeStrings = (values) => {
  if (!values) return [];
  const list = Array.isArray(values) ? values : [values];
  return list
    .map((value) => (value && typeof value === "object" ? namedRefLabel(value) : namedRefLabel(value)))
    .filter(Boolean);
};

const compactList = (...groups) => groups.flat().filter(Boolean);

const firstValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return null;
};

const formatMoney = (value, currency = "USD") => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value || "");
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: Number.isInteger(numeric) ? 0 : 2 }).format(numeric);
  } catch {
    return `$${numeric.toLocaleString()}`;
  }
};

const salaryLabel = (job) => {
  const direct = firstValue(job?.salaryExpectation, job?.salaryExpectations, job?.salaryRange, job?.compensation, job?.pay, job?.payRange);
  if (direct) return String(direct);
  const currency = job?.currency || job?.salaryCurrency || job?.payCurrency || "USD";
  const min = firstValue(job?.salaryMin, job?.minSalary, job?.minimumSalary, job?.payMin, job?.minPay);
  const max = firstValue(job?.salaryMax, job?.maxSalary, job?.maximumSalary, job?.payMax, job?.maxPay);
  const single = firstValue(job?.salary, job?.payRate, job?.hourlyRate);
  const cadence = titleCase(firstValue(job?.salaryPeriod, job?.payPeriod, job?.rateUnit, job?.payFrequency));
  const suffix = cadence ? ` / ${cadence.toLowerCase()}` : "";
  if (min && max) return `${formatMoney(min, currency)} - ${formatMoney(max, currency)}${suffix}`;
  if (min) return `From ${formatMoney(min, currency)}${suffix}`;
  if (max) return `Up to ${formatMoney(max, currency)}${suffix}`;
  if (single) return `${formatMoney(single, currency)}${suffix}`;
  return "Not set";
};

const formatDate = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const statusColor = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "OPEN" || normalized === "ACTIVE") return "green";
  if (normalized === "DRAFT") return "gold";
  if (normalized === "CLOSED" || normalized === "ARCHIVED") return "red";
  return "blue";
};

const locationLabel = (location) => {
  if (!location) return "";
  if (typeof location === "string") return isRawReferenceId(location) ? "" : location;
  return (
    readableText(location.label) ||
    readableText(location.name) ||
    readableText(location.address) ||
    readableText(location.formattedAddress) ||
    readableText([location.addressLine1, location.addressLine2, location.city, location.state, location.zipCode, location.country].filter(Boolean).join(", "))
  );
};

const personLabel = (value, fallback = "Member") => {
  if (!value) return fallback;
  if (typeof value === "string") return isRawReferenceId(value) ? fallback : value;
  return (
    readableText(value.name) ||
    readableText(value.fullName) ||
    readableText(value.displayName) ||
    readableText(value.email) ||
    readableText(value.user?.name) ||
    readableText(value.user?.email) ||
    fallback
  );
};

const questionLabel = (question) => firstText(question?.question, question?.title, question?.label, question?.text);

const questionMeta = (question) =>
  [
    titleCase(question?.answerType || question?.type || "Text"),
    question?.answerMetric || question?.metric,
    question?.isRequired === false ? "Optional" : "Required",
  ].filter(Boolean).join(" - ");

const isSameId = (left, right) => Boolean(left && right && String(left) === String(right));

const ownsJobPost = (job, actorId, workspaceId) => {
  if (!job) return false;
  return [
    job.ownerId,
    job.createdBy,
    job.createdById,
    job.posterId,
    job.poster?.id,
    job.userId,
    job.user?.id,
  ].some((value) => isSameId(value, actorId)) || [
    job.workspaceId,
    job.workspace?.id,
    job.organizationWorkspaceId,
    job.hiringWorkspaceId,
  ].some((value) => isSameId(value, workspaceId));
};

export default function JobOpportunityDetails() {
  const { jobOpportunityId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const [guestSession, setGuestSession] = useState(null);
  const actorId = user?.id || user?.userId || guestSession?.userId || guestSession?.sessionId || "";
  const guestAuthToken = isAuthenticated ? "" : bearerGuestToken(guestSession);
  const [job, setJob] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [hireMembers, setHireMembers] = useState([]);
  const [supportingLoading, setSupportingLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [signInPromptOpen, setSignInPromptOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const initializeGuest = async () => {
      if (isAuthenticated) {
        setGuestSession(null);
        return;
      }
      try {
        const session = await ensureGuestSession();
        if (!cancelled) setGuestSession(session || null);
      } catch {
        if (!cancelled) setGuestSession(null);
      }
    };
    initializeGuest();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const loadJob = useCallback(async () => {
    if (!jobOpportunityId) return;
    if (!isAuthenticated && !guestAuthToken) return;
    setLoading(true);
    setError("");
    try {
      setJob(await olreadiaService.jobOpportunities.get(jobOpportunityId, actorId, guestAuthToken));
    } catch (nextError) {
      setError(nextError?.message || "Could not load job opportunity.");
    } finally {
      setLoading(false);
    }
  }, [actorId, guestAuthToken, isAuthenticated, jobOpportunityId]);

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  const isOwner = Boolean(isAuthenticated && actorId) && ownsJobPost(job, actorId, selectedWorkspaceId);

  useEffect(() => {
    let cancelled = false;
    const loadSupportingDetails = async () => {
      if (!jobOpportunityId) return;
      if (!isAuthenticated && !guestAuthToken) return;
      setSupportingLoading(true);
      try {
        const [nextQuestions, nextHireMembers] = await Promise.all([
          isOwner ? olreadiaService.jobOpportunityQuestions.list(jobOpportunityId, actorId, guestAuthToken).catch(() => []) : Promise.resolve([]),
          olreadiaService.jobOpportunityHireMembers.list(jobOpportunityId, actorId, guestAuthToken).catch(() => []),
        ]);
        if (!cancelled) {
          setQuestions((nextQuestions || []).filter((question) => Boolean(questionLabel(question))));
          setHireMembers(nextHireMembers || []);
        }
      } finally {
        if (!cancelled) setSupportingLoading(false);
      }
    };
    loadSupportingDetails();
    return () => {
      cancelled = true;
    };
  }, [actorId, guestAuthToken, isAuthenticated, isOwner, jobOpportunityId]);

  const skills = useMemo(() => compactList(normalizeStrings(job?.skills || job?.requiredSkills), normalizeStrings(job?.otherRequiredSkills)), [job]);
  const specializations = useMemo(() => compactList(normalizeStrings(job?.specializations || job?.requiredSpecializations), normalizeStrings(job?.otherRequiredSpecializations)), [job]);
  const workAreas = useMemo(() => compactList(normalizeStrings(job?.requiredWorkAreas), normalizeStrings(job?.otherRequiredWorkAreas)), [job]);
  const schedules = useMemo(() => compactList(normalizeStrings(job?.workSchedules), job?.workSchedule ? [titleCase(job.workSchedule)] : []), [job]);
  const locations = useMemo(() => (Array.isArray(job?.requiredLocations) ? job.requiredLocations.map(locationLabel).filter(Boolean) : []), [job]);
  const canApply = !isOwner && !job?.applied;
  const employerName = namedRefLabel(job?.workspace) || namedRefLabel(job?.poster) || titleCase(job?.posterType) || "Employer post";
  const heroLocation = locations[0] || titleCase(job?.locationType) || "Location not set";
  const actionPath = `${paths.jobApplications}?jobOpportunityId=${encodeURIComponent(jobOpportunityId)}`;
  const manageApplicationsPath = `${paths.jobApplications}?context=hiring&jobOpportunityId=${encodeURIComponent(jobOpportunityId)}`;
  const interviewsPath = paths.jobOpportunityInterviewSessions(jobOpportunityId);
  const editPath = jobPostEditPath(jobOpportunityId);

  const handleClosePost = async () => {
    Modal.confirm({
      title: "Close job post",
      content: "Are you sure you want to close this job post? This will mark it as closed for applicants.",
      okText: "Close",
      okType: "danger",
      onOk: async () => {
        try {
          await olreadiaService.jobOpportunities.patch(jobOpportunityId, { status: "CLOSED" }, actorId);
          message.success("Job post closed.");
          await loadJob();
        } catch (nextError) {
          message.error(nextError?.message || "Unable to close job post.");
        }
      },
    });
  };

  const shareJobPost = async () => {
    const url = `${window.location.origin}${paths.jobOpportunityDetails(encodeURIComponent(jobOpportunityId))}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: job?.title || "Olreadia job post", url });
      } else {
        await navigator.clipboard.writeText(url);
        message.success("Job post link copied.");
      }
    } catch (nextError) {
      if (nextError?.name !== "AbortError") message.error(nextError?.message || "Unable to share this job post.");
    }
  };

  const startApply = () => {
    if (!isAuthenticated) {
      setSignInPromptOpen(true);
      return;
    }
    navigate(actionPath);
  };

  const detailItems = [
    { label: "Posted", value: formatDate(job?.createdAt), icon: <CalendarOutlined /> },
    { label: "Application window", value: `${formatDate(job?.postStartDate)} - ${formatDate(job?.postEndDate)}`, icon: <CalendarOutlined /> },
    { label: "Location type", value: titleCase(job?.locationType) || "Not set", icon: <EnvironmentOutlined /> },
    { label: "Schedule", value: schedules.join(", ") || "Not set", icon: <ClockCircleOutlined /> },
    { label: "Salary", value: salaryLabel(job), icon: <DollarOutlined /> },
    { label: "Experience", value: job?.requiredYearsOfExperience != null ? `${job.requiredYearsOfExperience}+ years` : "Not set", icon: <CheckCircleOutlined /> },
    { label: "Education", value: titleCase(job?.requiredEducationLevel) || "Not set", icon: <CheckCircleOutlined /> },
  ];
  const additionalDetails = [
    { label: "Responsibilities", value: job?.responsibilities },
    { label: "Qualifications", value: job?.qualifications },
    { label: "Requirements", value: job?.requirements },
    { label: "Benefits", value: job?.benefits },
    { label: "How to apply", value: firstText(job?.applicationInstructions, job?.howToApply, job?.applyInstructions) },
  ].filter((item) => hasRichText(item.value));
  const requirementGroups = [
    { label: "Required skills", values: skills },
    { label: "Specializations", values: specializations },
    { label: "Work areas", values: workAreas },
  ].filter((group) => group.values.length);

  if (loading) return <div className="job-opportunity-details-page"><Skeleton active /></div>;

  if (error) {
    return (
      <div className="job-opportunity-details-page">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} className="job-opportunity-details-back">Back</Button>
        <Alert type="error" showIcon message="Unable to load job opportunity" description={error} action={<Button onClick={loadJob}>Retry</Button>} />
      </div>
    );
  }

  return (
    <div className="job-opportunity-details-page">
      <div className="job-opportunity-details-layout">
        <main className="job-opportunity-details-main">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} className="job-opportunity-details-back">Back</Button>

          <section className="job-opportunity-details-hero">
            <div className="job-opportunity-details-hero__top">
              <span className="job-opportunity-details-hero__icon"><SolutionOutlined /></span>
              <Space wrap>
                <Button icon={<ShareAltOutlined />} onClick={shareJobPost}>Share</Button>
                <Tag color={statusColor(job?.status)}>{titleCase(job?.status || "OPEN")}</Tag>
              </Space>
            </div>
            <Title level={2}>{job?.title || "Untitled job post"}</Title>
            <Text className="job-opportunity-details-hero__subtitle">{employerName}</Text>
            <div className="job-opportunity-details-hero__meta">
              <span><EnvironmentOutlined /> {heroLocation}</span>
              <span><CalendarOutlined /> Posted {formatDate(job?.createdAt)}</span>
              <span><ClockCircleOutlined /> {schedules.join(", ") || "Schedule not set"}</span>
            </div>
            <div className="job-opportunity-details-actions">
              {canApply ? (
                <Button type="primary" icon={<SendOutlined />} onClick={startApply}>
                  Apply
                </Button>
              ) : !isOwner ? (
                <Button icon={<CheckCircleOutlined />} disabled>
                  Already Applied
                </Button>
              ) : (
                <>
                  <Button type="primary" icon={<TeamOutlined />} onClick={() => navigate(manageApplicationsPath)}>
                    Applications
                  </Button>
                  <Button className="job-opportunity-details-action--interviews" icon={<CalendarOutlined />} onClick={() => navigate(interviewsPath)}>
                    Interview Session Templates
                  </Button>
                  <Button className="job-opportunity-details-action--edit" icon={<EditOutlined />} onClick={() => navigate(editPath, { state: { jobOpportunityId, jobPost: job } })}>
                    Edit
                  </Button>
                  <Button danger icon={<CloseOutlined />} onClick={handleClosePost}>
                    Close post
                  </Button>
                </>
              )}
            </div>
          </section>

          {hasRichText(job?.description) ? (
            <Card className="job-opportunity-details-section job-opportunity-details-description-card">
              <Title level={4}>Description</Title>
              <div
                className="job-opportunity-details-description olreadia-rich-text"
                dangerouslySetInnerHTML={richTextMarkup(job.description)}
              />
            </Card>
          ) : null}

          <Card className="job-opportunity-details-section">
            <Title level={4}>Overview</Title>
            <div className="job-opportunity-details-grid">
              {detailItems.map((item) => (
                <div key={item.label} className="job-opportunity-details-fact">
                  <span>{item.icon}</span>
                  <div>
                    <Text type="secondary">{item.label}</Text>
                    <strong>{item.value}</strong>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="job-opportunity-details-section">
            <Title level={4}>Requirements</Title>
            {requirementGroups.length ? (
              <div className="job-opportunity-details-requirements">
                {requirementGroups.map((group) => (
                  <div key={group.label}>
                    <Text>{group.label}</Text>
                    <Space wrap size={[6, 6]}>
                      {group.values.map((item) => <Tag key={item}>{item}</Tag>)}
                    </Space>
                  </div>
                ))}
              </div>
            ) : (
              <Text type="secondary">No requirements listed yet.</Text>
            )}
          </Card>

          {locations.length ? (
            <Card className="job-opportunity-details-section">
              <Title level={4}>Locations</Title>
              <div className="job-opportunity-details-location-list">
                {locations.map((item) => (
                  <div key={item} className="job-opportunity-details-location-row">
                    <EnvironmentOutlined />
                    <Text>{item}</Text>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}

          {additionalDetails.map((item) => (
            <Card key={item.label} className="job-opportunity-details-section">
              <Title level={4}>{item.label}</Title>
              <div className="olreadia-rich-text" dangerouslySetInnerHTML={richTextMarkup(item.value)} />
            </Card>
          ))}

          {isOwner && (questions.length || supportingLoading) ? (
            <Card className="job-opportunity-details-section">
              <div className="job-opportunity-details-section__header">
                <div>
                  <Title level={4}>Application Questions</Title>
                  <Text type="secondary">Candidates answer these while applying.</Text>
                </div>
              </div>
              {supportingLoading ? <Skeleton active paragraph={{ rows: 2 }} /> : null}
              {!supportingLoading && !questions.length ? <Text type="secondary">No questions attached yet.</Text> : null}
              <div className="job-opportunity-details-question-list">
                {questions.map((question, index) => (
                  <div key={question.id || `${questionLabel(question)}-${index}`} className="job-opportunity-details-question">
                    <span><QuestionCircleOutlined /></span>
                    <div>
                      <strong>{questionLabel(question)}</strong>
                      <Text type="secondary">{questionMeta(question)}</Text>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}
        </main>

        <aside className="job-opportunity-details-side">
          <div className="job-opportunity-details-action-rail">
            <Card className="job-opportunity-details-side-card">
              <Text className="job-opportunity-details-section__title">Hiring workflow</Text>
              <div className="job-opportunity-details-workflow">
                <div><TeamOutlined /><span>Applications</span><strong>{isOwner ? "Manage" : "Apply"}</strong></div>
                <div><CalendarOutlined /><span>Interviews</span><strong>Next steps</strong></div>
                {isOwner ? <div><QuestionCircleOutlined /><span>Questions</span><strong>{questions.length || 0}</strong></div> : null}
                {isOwner ? <div><UserAddOutlined /><span>Hiring team</span><strong>{hireMembers.length || 0}</strong></div> : null}
              </div>
            </Card>

            <Card className="job-opportunity-details-side-card">
              <Text className="job-opportunity-details-section__title">Post snapshot</Text>
              <div className="job-opportunity-details-snapshot">
                <div><InfoCircleOutlined /><span>Status</span><strong>{titleCase(job?.status || "Open")}</strong></div>
                <div><DollarOutlined /><span>Salary</span><strong>{salaryLabel(job)}</strong></div>
                <div><EnvironmentOutlined /><span>Location</span><strong>{heroLocation}</strong></div>
                <div><CheckCircleOutlined /><span>Education</span><strong>{titleCase(job?.requiredEducationLevel) || "Not set"}</strong></div>
              </div>
            </Card>

            {isOwner && (hireMembers.length || supportingLoading) ? (
              <Card className="job-opportunity-details-side-card">
                <Text className="job-opportunity-details-section__title">Hiring team</Text>
                {supportingLoading ? <Skeleton active paragraph={{ rows: 2 }} /> : null}
                {!supportingLoading ? (
                  <div className="job-opportunity-details-member-list">
                    {hireMembers.slice(0, 4).map((member, index) => (
                      <div key={member.id || `${member.userId}-${index}`}>
                        <span><UserAddOutlined /></span>
                        <div>
                          <strong>{personLabel(member, `Member ${index + 1}`)}</strong>
                          <Text type="secondary">{titleCase(member.role || "Hiring team")}</Text>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </Card>
            ) : null}
          </div>

          <SidePromotionSection className="job-opportunity-details-promotions" />
        </aside>
      </div>
      <SignInRequiredModal
        open={signInPromptOpen}
        onClose={() => setSignInPromptOpen(false)}
        title="Login to apply"
        message="You can view this job post without logging in. Log in or create an account to submit an application."
      />
    </div>
  );
}
